import { useState } from 'react';
import { Card, CardContent, Box, Typography, Chip, IconButton, Menu, MenuItem, CircularProgress, Avatar } from '@mui/material';
import { RefreshCw, Trash2, MoreVertical, Database, MessageSquare, Mail, Calendar } from 'lucide-react';
import { authenticatedFetch } from '@diggens/api-client';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

interface Connection {
  id: string;
  name: string;
  source_system: string;
  status: string;
  sync_mode: string;
  sync_frequency: string;
  last_sync_at: string | null;
  next_sync_at: string | null;
  error_message: string | null;
  created_at: string;
}

interface ConnectionCardProps {
  connection: Connection;
  onUpdate: () => void;
}

const getSourceIcon = (source: string) => {
  switch (source) {
    case 'salesforce':
      return <Database size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />;
    case 'slack':
      return <MessageSquare size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />;
    case 'gmail':
      return <Mail size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />;
    case 'google-calendar':
      return <Calendar size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />;
    default:
      return <Database size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />;
  }
};

const getSourceColor = (source: string) => {
  switch (source) {
    case 'salesforce':
      return '#00A1E0';
    case 'slack':
      return '#4A154B';
    case 'gmail':
      return '#EA4335';
    case 'google-calendar':
      return '#4285F4';
    default:
      return '#6366f1';
  }
};

const formatDate = (date: string | null) => {
  if (!date) return 'Never';
  return new Date(date).toLocaleString();
};

export function ConnectionCard({ connection, onUpdate }: ConnectionCardProps) {
  const [syncing, setSyncing] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleSync = async (mode: 'full' | 'incremental') => {
    setSyncing(true);
    setAnchorEl(null);

    try {
      const response = await authenticatedFetch(`/api/data/connections/${connection.id}/sync`, {
        method: 'POST',
        body: JSON.stringify({ mode }),
      });

      if (!response.ok) {
        throw new Error('Sync failed');
      }

      // Refresh the connection data
      setTimeout(() => {
        onUpdate();
      }, 1000);
    } catch (error) {
      console.error('Failed to trigger sync:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to disconnect ${connection.name}?`)) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/data/connections/${connection.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete connection');
      }

      onUpdate();
    } catch (error) {
      console.error('Failed to delete connection:', error);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: getSourceColor(connection.source_system), width: 48, height: 48 }}>{getSourceIcon(connection.source_system)}</Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {connection.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {connection.source_system}
            </Typography>
          </Box>

          <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} disabled={syncing}>
            <MoreVertical size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
          </IconButton>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem onClick={() => handleSync('incremental')}>
              <RefreshCw size={ICON_SIZES.md} strokeWidth={ICON_STROKE} style={{ marginRight: 8 }} />
              Sync Now
            </MenuItem>
            <MenuItem onClick={() => handleSync('full')}>
              <RefreshCw size={ICON_SIZES.md} strokeWidth={ICON_STROKE} style={{ marginRight: 8 }} />
              Full Sync
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <Trash2 size={ICON_SIZES.md} strokeWidth={ICON_STROKE} style={{ marginRight: 8 }} />
              Disconnect
            </MenuItem>
          </Menu>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={connection.status} color={connection.status === 'active' ? 'success' : 'error'} size="small" />
          <Chip label={connection.sync_mode} variant="outlined" size="small" />
          <Chip label={connection.sync_frequency} variant="outlined" size="small" />
        </Box>

        {connection.error_message && (
          <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
            Error: {connection.error_message}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Last sync: {formatDate(connection.last_sync_at)}
          </Typography>
          {connection.next_sync_at && (
            <Typography variant="caption" color="text.secondary">
              Next sync: {formatDate(connection.next_sync_at)}
            </Typography>
          )}
        </Box>

        {syncing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Syncing...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
